/**
 * companyRule.js — 系列会社ありグループの「会社名」検証ルール
 * ------------------------------------------------------------------
 * 「グループ会社・系列会社構造」PJ。Group.has_affiliates が true のグループでは
 * User.company は「系列会社名」であり、系列会社マスタ（Group.company[] の active な名称）に
 * 登録された名称のみ入力できる。サーバー側の検証と同一規則・同一エラー文言（変更時は両方を同期）。
 *
 *   - company は前後空白を除去して判定する
 *   - role を問わず（受講者・管理者とも、いずれかの系列会社に属する）次をエラーとする
 *     - 空の場合
 *     - マスタ（active）に無い場合
 *
 * has_affiliates が false / undefined のグループでは enabled=false となり、検証は常に null（問題なし）を返す。
 *
 * 系列会社名の取得元（2026-10）: サーバーの GET /api/group/:group_code/company-list を唯一の取得元とする
 * （有効な確定組織表に系列会社ノードがあればその名称、無ければグループ編集の系列会社マスタ）。
 * fetchCompanyRule / resolveCompanyRule / useCompanyRule が API を呼び、失敗時のみ buildCompanyRule(group)
 * （取得済みのグループ情報）へフォールバックする。has_affiliates でないグループでは API を呼ばない。
 * 上記 3 関数以外は純関数。
 */
import { useEffect, useMemo, useState } from 'react';
import Axios from 'axios';
import {
  normalizeRecord,
  buildCompanyPathConflictRows,
} from './orgRosterHelper';

// 文字列化して前後空白を除去する（undefined / null は空文字）
const trimText = (value) =>
  value === undefined || value === null ? '' : String(value).trim();

/**
 * グループ情報から会社名ルールを作る。
 * @param {Object} group - Group ドキュメント（has_affiliates / company を含む）
 * @returns {{ enabled: boolean, names: string[] }} has_affiliates でなければ { enabled:false, names:[] }
 */
export const buildCompanyRule = (group) => {
  if (!group || group.has_affiliates !== true) {
    return { enabled: false, names: [] };
  }
  const names = (Array.isArray(group.company) ? group.company : [])
    .filter((c) => c && c.active !== false)
    .map((c) => trimText(c.name))
    .filter((name) => name !== '');
  return { enabled: true, names };
};

/**
 * サーバーから系列会社名の一覧（会社名ルール）を取得する。
 * GET /api/group/:group_code/company-list?date=YYYY-MM-DD
 * @param {string} groupCode
 * @param {string} [date] 基準日 'YYYY-MM-DD'（省略時はサーバー側で今日）
 * @returns {Promise<{ enabled: boolean, names: string[], source: 'org'|'group'|null, version: Object|null }|null>}
 *   取得失敗時は null（呼び出し側は buildCompanyRule(group) へフォールバックする）
 */
export const fetchCompanyRule = async (groupCode, date) => {
  if (!groupCode) return null;
  try {
    const query = date ? `?date=${encodeURIComponent(date)}` : '';
    const res = await Axios.get(
      `/api/group/${encodeURIComponent(groupCode)}/company-list${query}`
    );
    const data = res && res.data;
    if (!data || !data.success) return null;
    const enabled = data.enabled === true;
    const names = enabled
      ? (Array.isArray(data.names) ? data.names : [])
          .map((name) => trimText(name))
          .filter((name) => name !== '')
      : [];
    return {
      enabled,
      names,
      source: data.source === 'org' || data.source === 'group' ? data.source : null,
      version: data.version || null,
    };
  } catch (error) {
    console.log('系列会社一覧の取得に失敗しました', error);
    return null;
  }
};

/**
 * 会社名ルールを解決する（API 優先・失敗時はグループ情報から作る）。
 * has_affiliates でないグループ（group が無い場合を含む）は API を呼ばず buildCompanyRule(group)
 * （= enabled:false）を返す＝系列会社なしグループの挙動は従来と同一。
 * @param {string} groupCode
 * @param {Object} group - Group ドキュメント（has_affiliates / company を含む）
 * @param {string} [date]
 */
export const resolveCompanyRule = async (groupCode, group, date) => {
  const fallback = buildCompanyRule(group);
  if (!fallback.enabled || !groupCode) return fallback;
  const fetched = await fetchCompanyRule(groupCode, date);
  return fetched || fallback;
};

/**
 * React フック版 resolveCompanyRule。グループ選択（groupCode / group の変化）ごとに 1 回 API を呼ぶ。
 * 取得完了までと取得失敗時は buildCompanyRule(group) の結果を返す。
 * @param {string} groupCode
 * @param {Object} group
 * @returns {{ enabled: boolean, names: string[], source?: string|null, version?: Object|null }}
 */
export const useCompanyRule = (groupCode, group) => {
  const fallback = useMemo(() => buildCompanyRule(group), [group]);
  const affiliate = fallback.enabled;
  const [fetched, setFetched] = useState(null); // { groupCode, rule }
  useEffect(() => {
    if (!groupCode || !affiliate) return undefined;
    let cancelled = false;
    fetchCompanyRule(groupCode).then((rule) => {
      if (!cancelled) setFetched({ groupCode, rule });
    });
    return () => {
      cancelled = true;
    };
  }, [groupCode, affiliate]);
  if (affiliate && fetched && fetched.groupCode === groupCode && fetched.rule) {
    return fetched.rule;
  }
  return fallback;
};

/**
 * 1 行分の会社名を検証する。
 * @param {{ enabled: boolean, names: string[] }} rule - buildCompanyRule の戻り値
 * @param {*} company - 会社名
 * @param {*} _role - 互換のため残している引数（判定には使用しない。全ロール必須）
 * @returns {string|null} 問題なければ null、あればエラー文字列
 */
export const validateCompany = (rule, company, _role) => {
  if (!rule || !rule.enabled) return null;
  const value = trimText(company);
  if (value === '') {
    return '会社名が未入力です（系列会社ありのグループでは必須です）';
  }
  if (!(rule.names || []).includes(value)) {
    return `会社名「${value}」は系列会社マスタに登録されていません`;
  }
  return null;
};

/**
 * 名簿の各行の会社名を検証し、エラー行だけを validate-roster と同じ形で返す。
 * @param {{ enabled: boolean, names: string[] }} rule
 * @param {Object[]} records - 名簿の行（fileJSON。行番号は 1 起点＝配列 index + 1）
 * @param {Object} [options]
 * @param {Function} [options.isTarget] - (record, index) => false の行は検証しない
 * @returns {Array<{ row: number, ok: false, error: string, suggestions: [], field: 'company' }>}
 */
export const validateCompanyRows = (rule, records, options = {}) => {
  if (!rule || !rule.enabled || !Array.isArray(records)) return [];
  const isTarget = options.isTarget || (() => true);
  const results = [];
  records.forEach((record, index) => {
    if (!isTarget(record, index)) return;
    const error = validateCompany(rule, record ? record.company : undefined);
    if (error) {
      results.push({ row: index + 1, ok: false, error, suggestions: [], field: 'company' });
    }
  });
  return results;
};

/**
 * 名簿に company 列があるか（どれか 1 行でも company キーを持つか）。
 * CSV（papaparse header:true）・Excel パースともヘッダーにある列は全行にキーが入る。
 */
export const hasCompanyColumn = (records) =>
  Array.isArray(records) &&
  records.some((record) => record && Object.prototype.hasOwnProperty.call(record, 'company'));

/**
 * 行に company キーがあるか（名簿に company 列がある、または所属フルパスから会社名を補完した行）。
 */
export const hasCompanyKey = (record) =>
  !!record && Object.prototype.hasOwnProperty.call(record, 'company');

/**
 * 系列会社ありグループの名簿について、クライアント側の会社名エラー行を集める。
 *   ① 「会社名」列と「所属フルパス」先頭の会社名の食い違い（conflict:true）
 *   ② 会社名ルール（rule.names）との照合（所属フルパスの会社名で補完したうえで判定）
 * 同じ行に ① ② があれば 2 件になる（mergeRowErrors が 1 行にまとめる）。
 * @param {{ enabled: boolean, names: string[] }} rule
 * @param {Object[]} records - 名簿の行（未正規化の fileJSON）
 * @param {Object} [options]
 * @param {Function} [options.isTarget] - (正規化済み record, index) => false の行は ② を検証しない
 * @returns {Array} validateCompanyRows と同じ形のエラー行
 */
export const collectCompanyErrors = (rule, records, options = {}) => {
  if (!rule || !rule.enabled || !Array.isArray(records)) return [];
  const normalized = records.map((record) => normalizeRecord(record, { affiliate: true }));
  const conflicts = buildCompanyPathConflictRows(records, { affiliate: true });
  const checked = validateCompanyRows(rule, normalized, options);
  return [...conflicts, ...checked];
};

/**
 * 組織表検証（validate-roster）のエラー行と会社名検証のエラー行を 1 つにまとめる。
 * 同じ行に両方ある場合は 1 行にまとめ、error を「／」で連結する（候補は組織表検証のものを維持）。
 * サーバーが会社名エラー（field:'company'）を返した行は、クライアントの会社名照合エラー（conflict でないもの）を
 * 重ねない（同じ判定の二重表示を避けるため。サーバーの候補（近い系列会社名）を優先する）。
 * @param {Array} orgErrors - validate-roster のエラー行（ok:false）
 * @param {Array} companyErrors - validateCompanyRows / collectCompanyErrors の結果
 * @returns {Array} 行番号順のエラー行
 */
export const mergeRowErrors = (orgErrors, companyErrors) => {
  const byRow = new Map();
  const serverCompanyRows = new Set();
  (orgErrors || []).forEach((r) => {
    if (r && r.ok === false) {
      byRow.set(r.row, { ...r });
      if (r.field === 'company') serverCompanyRows.add(r.row);
    }
  });
  (companyErrors || []).forEach((r) => {
    if (!r) return;
    if (serverCompanyRows.has(r.row) && !r.conflict) return;
    const current = byRow.get(r.row);
    if (current) {
      byRow.set(r.row, {
        ...current,
        error: [current.error, r.error].filter((e) => e).join('／'),
        field: 'company',
      });
    } else {
      byRow.set(r.row, { ...r });
    }
  });
  return Array.from(byRow.values()).sort((a, b) => a.row - b.row);
};

/**
 * エラー見出しの切替用に、会社名エラーと所属エラーの有無を数える（行単位ではなく件数の目安）。
 * サーバー（validate-roster）の field:'company' は会社名エラー、それ以外は所属エラーとして数える。
 * @param {Array} orgErrors - validate-roster のエラー行（ok:false）
 * @param {Array} companyErrors - クライアント側の会社名エラー行
 * @returns {{ company: number, department: number }}
 */
export const countErrorKinds = (orgErrors, companyErrors) => {
  const org = (orgErrors || []).filter((r) => r && r.ok === false);
  const serverCompany = org.filter((r) => r.field === 'company').length;
  return {
    company: (companyErrors || []).length + serverCompany,
    department: org.length - serverCompany,
  };
};

/**
 * サーバーの 400 応答（company_errors）から先頭 N 件の「email: error」を組み立てる。
 * company_errors が無い応答では空文字を返す（従来のエラー表示と同一になる）。
 * @param {*} error - axios のエラーオブジェクト
 * @param {number} [limit=5]
 * @returns {string} 例: '（a@example.com: 会社名が未入力です…、ほか 3 件）'
 */
export const formatCompanyErrorDetail = (error, limit = 5) => {
  const data = error && error.response && error.response.data;
  const list = data && Array.isArray(data.company_errors) ? data.company_errors : [];
  if (list.length === 0) return '';
  const head = list
    .slice(0, limit)
    .map((e) => `${(e && e.email) || '—'}: ${(e && e.error) || ''}`)
    .join('、');
  const rest = list.length > limit ? `、ほか ${list.length - limit} 件` : '';
  return `（${head}${rest}）`;
};
